import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import BlockProse from "@/components/blocks/renderers/BlockProse";

// Buttons that link to same-page anchors (e.g. #openings) render as raw <a>
// so React Router doesn't reset the scroll. Real cross-page routes still use
// <Link> so client-side navigation stays snappy.
function CtaButton({ label, href, variant, testId }) {
  if (!label) return null;
  const isAnchor = href && href.startsWith("#");
  const className =
    variant === "outline"
      ? "rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 px-8 h-12 text-sm font-semibold"
      : "bg-sky-600 hover:bg-sky-700 text-white rounded-full px-8 h-12 text-sm font-semibold";
  const contents = (
    <>
      {label}
      {variant !== "outline" && <ArrowRight className="ml-2 h-4 w-4" />}
    </>
  );
  if (isAnchor) {
    return (
      <a href={href}>
        <Button data-testid={testId} className={className} variant={variant === "outline" ? "outline" : "default"}>
          {contents}
        </Button>
      </a>
    );
  }
  return (
    <Link to={href || "/contact"}>
      <Button data-testid={testId} className={className} variant={variant === "outline" ? "outline" : "default"}>
        {contents}
      </Button>
    </Link>
  );
}

/**
 * Two-column hero with copy on the left and an image on the right. Buttons
 * support both real routes (via react-router Link) and same-page anchors
 * (via plain <a>) so we can wire up "View Open Positions" -> #openings, etc.
 */
export default function HeroSplitBlock({ data }) {
  const {
    title,
    title_accent,
    subtitle,
    image,
    primary_label,
    primary_href,
    secondary_label,
    secondary_href,
  } = data || {};

  return (
    <section className="pt-32 pb-14 lg:pt-40 lg:pb-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
        >
          <div>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
              {title}
              {title_accent ? <> <span className="text-sky-600">{title_accent}</span></> : null}
            </h1>
            {subtitle && (
              <div className="mt-5">
                <BlockProse
                  value={subtitle}
                  proseClassName="prose prose-slate max-w-none prose-p:text-slate-500 prose-a:text-sky-600 prose-a:no-underline hover:prose-a:underline text-base lg:text-lg"
                  plainClassName="text-base lg:text-lg text-slate-500 leading-relaxed whitespace-pre-line"
                />
              </div>
            )}
            {(primary_label || secondary_label) && (
              <div className="mt-6 flex flex-wrap gap-3">
                <CtaButton
                  label={primary_label}
                  href={primary_href}
                  testId="hero-split-primary-btn"
                />
                <CtaButton
                  label={secondary_label}
                  href={secondary_href}
                  variant="outline"
                  testId="hero-split-secondary-btn"
                />
              </div>
            )}
          </div>
          {image && (
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img
                src={image}
                alt={title || ""}
                className="w-full h-72 lg:h-80 object-cover"
                loading="lazy"
              />
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
